import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import {
  MapPin,
  Clock,
  DollarSign,
  Building,
  FileText,
  GraduationCap,
  Users,
  Calendar,
  Edit3,
  Trash2,
  ArrowLeft,
  Star,
  UserCheck,
  Mail,
  Phone,
  Eye,
  Briefcase,
  TrendingUp,
  Share2,
  BookOpen,
} from "lucide-react";
import api, { FILES_URL } from '../../../utils/Api';




const JobOfferDetails = () => {
  const { t } = useTranslation('jobOffer');
  const { id } = useParams();
  const navigate = useNavigate();


  const [jobOffer, setJobOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [applications, setApplications] = useState({})

  // Fetch job offer data on component mount
  useEffect(() => {
    const fetchJobOffer = async () => {
      try {
        setLoading(true);
        const response = await api.get("/jobOffer/" + id);
        setJobOffer(response.data.data);
      } catch (error) {
        console.error("Error fetching job offer:", error);
      }
    };

    const fetchApplications = async () => {
      try {

        const response = await api.get(`/jobOffer/applications/${id}`);

        setApplications(response.data.data || []);

      } catch (error) {
        console.error("Error fetching applications:", error);
        setApplications([]);
      } finally {
        setLoading(false);
      }
    };

    if (id) {

      fetchJobOffer();
      fetchApplications()
    }
  }, [id]);

  const handleEdit = () => {
    navigate(`/dashboard/job-offers/${id}/edit`);
  };

  const handleDelete = async () => {
    if (window.confirm(t("delete.jobOffer.confirm"))) {
      try {
        setIsDeleting(true);
        await api.delete("/jobOffer/" + id);
        navigate("/job-offers");
      } catch (error) {
        console.error("Error deleting job offer:", error);
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleString();
  };

  const getContractTypeLabel = (type) => {
    const contractTypes = {
      cdi: t("contract.type.cdi"),
      cdd: t("contract.type.cdd"),
      freelance: t("contract.type.freelance"),
      internship: t("contract.type.internship")
    };
    return contractTypes[type] || type;
  };

  const getExperienceLevelLabel = (level) => {
    const experienceLevels = {
      1: t("experience.level.junior"),
      2: t("experience.level.midlevel"),
      3: t("experience.level.senior")
    };
    return experienceLevels[level] || level;
  };

  // Show loading spinner while fetching data
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">{t("loading")}</p>
        </div>
      </div>
    );
  }

  if (!jobOffer) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            {t("jobOffer.not.found")}
          </h2>
          <button
            onClick={() => navigate("/job-offers")}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {t("back.to.jobOffers")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Header with Back Button and Action Buttons */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4 sm:mb-0 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            {t("back")}
          </button>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleEdit}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              <Edit3 className="w-4 h-4 mr-2" />
              {t("jobOffer.edit")}
            </button>
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {isDeleting ? t("jobOffer.deleting") : t("jobOffer.delete")}
            </button>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 shadow-xl rounded-lg overflow-hidden">
          {/* Hero Section with Image and Title */}
          <div className="relative">
            {jobOffer.imageUrl ? (
              <div className="h-96 overflow-hidden">
                <img
                  src={FILES_URL + jobOffer.imageUrl
                  }
                  alt={jobOffer.title?.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black bg-opacity-20"></div>
              </div>
            ) : (
              <div className="h-96 bg-gradient-to-r from-blue-600 to-blue-800"></div>
            )}

            {/* Title Overlay */}
            <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <h1 className="text-4xl font-bold mb-2">{jobOffer.title?.title}</h1>
                  {jobOffer.company && (
                    <p className="text-xl text-blue-100 flex items-center">
                      <Building className="w-5 h-5 mr-2" />
                      {jobOffer.company.company_name}
                    </p>
                  )}
                  {(jobOffer.title?.sector?.name || jobOffer.title?.customSector) && (
                    <p className="text-lg text-blue-100 mt-1">
                      {jobOffer.title.sector?.name || jobOffer.title.customSector}
                    </p>
                  )}
                  <p className="text-sm text-blue-100 mt-2">
                    {t("jobOffer.created")}: {formatDateTime(jobOffer.createdAt)}
                  </p>
                </div>

                {/* Stats */}
                <div className="mt-4 sm:mt-0 flex flex-col space-y-2">
                  {jobOffer.isFeatured && (
                    <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
                      <Star className="w-4 h-4 mr-2" />
                      {t("jobOffer.featured")}
                    </span>
                  )}
                  <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-green-100 text-green-800">
                    <Eye className="w-4 h-4 mr-2" />
                    {jobOffer.viewsCount} {t("views")}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="p-8">
            {/* Description */}
            {jobOffer.description && (
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  {t("jobOffer.description")}
                </h2>
                <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
                  {jobOffer.description}
                </p>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left Column */}
              <div className="space-y-8">
                {/* Location */}
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                    <MapPin className="w-6 h-6 mr-3 text-blue-600" />
                    {t("jobOffer.location")}
                  </h3>
                  <div className="space-y-2 text-gray-700 dark:text-gray-300">
                    {jobOffer.address?.line1 && (
                      <p className="text-lg">{jobOffer.address.line1}</p>
                    )}
                    {jobOffer.address?.line2 && (
                      <p className="text-lg">{jobOffer.address.line2}</p>
                    )}
                    <p className="text-lg font-medium">
                      {jobOffer.address?.zipCode} {jobOffer.address?.city}
                    </p>
                    {jobOffer.address?.wilaya && (
                      <p className="text-lg">{jobOffer.address.wilaya}</p>
                    )}
                  </div>
                </div>

                {/* Contract & Experience */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                      <Briefcase className="w-5 h-5 mr-2 text-green-600" />
                      {t("contract.type")}
                    </h3>
                    <span className="inline-flex items-center px-3 py-2 rounded-full text-sm font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                      {getContractTypeLabel(jobOffer.contractType)}
                    </span>
                  </div>

                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                      <TrendingUp className="w-5 h-5 mr-2 text-orange-600" />
                      {t("experience.level")}
                    </h3>
                    <span className="inline-flex items-center px-3 py-2 rounded-full text-sm font-medium bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200">
                      {getExperienceLevelLabel(jobOffer.experienceLevel)}
                    </span>
                  </div>
                </div>

                {/* Skills */}
                {jobOffer.required_skills && jobOffer.required_skills.length > 0 && (
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                      <Users className="w-6 h-6 mr-3 text-purple-600" />
                      {t("jobOffer.required.skills")}
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {jobOffer.required_skills.map((skill) => (
                        <span
                          key={skill._id}
                          className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200"
                        >
                          {skill.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Tags */}
                {jobOffer.tags && jobOffer.tags.length > 0 && (
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                      <FileText className="w-6 h-6 mr-3 text-indigo-600" />
                      {t("jobOffer.tags")}
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {jobOffer.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-3 py-2 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Right Column */}
              <div className="space-y-8">
                {/* Dates */}
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                    <Calendar className="w-6 h-6 mr-3 text-orange-600" />
                    {t("jobOffer.dates")}
                  </h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-400">{t("start.date")}:</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {formatDate(jobOffer.startAt) || 'N/A'}
                      </span>
                    </div>
                    {jobOffer.endAt && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600 dark:text-gray-400">{t("end.date")}:</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {formatDate(jobOffer.endAt)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Education Level */}
                {jobOffer.educationLevel && (
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                      <GraduationCap className="w-6 h-6 mr-3 text-red-600" />
                      {t("jobOffer.education")}
                    </h3>
                    <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                      {t(`education.levels.${jobOffer.educationLevel}`)}
                    </span>
                  </div>
                )}

                {/* Company Info */}
                {jobOffer.company && (
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                      <Building className="w-6 h-6 mr-3 text-blue-600" />
                      {t("company.info")}
                    </h3>
                    <div className="space-y-3">
                      <div className="flex items-center">

                        <div>
                          <h4 className="font-medium text-gray-900 dark:text-white">
                            {jobOffer.company.company_name}
                          </h4>
                          {jobOffer.company.sector && (
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {jobOffer.company.sector.name}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="flex gap-2 pt-2">
                        {jobOffer.company.email && (
                          <button
                            onClick={() => window.open(`mailto:${jobOffer.company.email}`, '_blank')}
                            className="flex items-center justify-center px-3 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors"
                          >
                            <Mail className="w-4 h-4 mr-1" />
                            {t("contact")}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Stats */}
                {jobOffer.trendingScore > 0 && (
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                      <TrendingUp className="w-6 h-6 mr-3 text-green-600" />
                      {t("trending.score")}
                    </h3>
                    <div className="text-3xl font-bold text-green-600 dark:text-green-400">
                      {jobOffer.trendingScore}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobOfferDetails;
